import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { PlatformApiError, type CreateJobOpening, type JobOpening, type Viewer } from '@sfc/sdk';
import { platform } from '../api/client';
import { Empty } from '../ui/Empty';
import { Icon } from '../ui/Icon';
import { StatStrip } from '../ui/StatStrip';

const EMPTY: CreateJobOpening = {
  slug: '',
  title: '',
  department: '',
  location: 'Remote',
  employmentType: 'Part-time',
  description: '',
  requirements: [],
  responsibilities: [],
};

/**
 * Job openings.
 *
 * A draft is invisible to the world; publishing is the act that puts it on the
 * careers page, and closing takes it off without destroying it. The record has
 * to outlive the advert, because applications point at it.
 */
export function JobsPanel({ viewer }: { viewer: Viewer }): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const mayWrite = viewer.capabilities.includes('jobs:write');

  const [openings, setOpenings] = useState<JobOpening[] | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [draft, setDraft] = useState<CreateJobOpening>(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setOpenings(await platform.jobs.list(organizationId, signal));
      } catch (error) {
        if (signal?.aborted) return;
        setOpenings([]);
        setProblem(describe(error));
      }
    },
    [organizationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const stats = useMemo(() => {
    const rows = openings ?? [];
    const open = rows.filter((opening) => opening.status === 'open').length;
    return [
      { label: 'Openings', value: rows.length },
      { label: 'Live on the site', value: open, tone: open > 0 ? ('up' as const) : undefined },
      { label: 'Drafts', value: rows.filter((opening) => opening.status === 'draft').length },
    ];
  }, [openings]);

  async function attempt(work: () => Promise<unknown>): Promise<void> {
    setProblem(undefined);
    try {
      await work();
      await load();
    } catch (error) {
      setProblem(describe(error));
    }
  }

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    try {
      await attempt(() =>
        platform.jobs.create(organizationId, {
          ...draft,
          // One per line is how somebody writing an advert thinks about them.
          requirements: splitLines(draft.requirements as unknown as string),
          responsibilities: splitLines(draft.responsibilities as unknown as string),
        }),
      );
      setDraft(EMPTY);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-labelledby="section-heading">
      <StatStrip stats={stats} testId="job-stats" />

      <div className="panel">
        <div className="panel__head">
          <h2>All openings</h2>
          <span className="count" data-testid="job-count">
            {openings ? `${openings.length}` : '…'}
          </span>
        </div>

        {openings && openings.length === 0 ? (
          <Empty
            icon="jobs"
            title="No openings yet"
            hint="Create a draft below. Nothing reaches the careers page until you publish it."
          />
        ) : null}

        <ul className="ledger" data-testid="job-openings">
          {(openings ?? []).map((opening) => (
            <li key={opening.id} className="ledger__row ledger__row--plain" data-testid={`job-${opening.slug}`}>
              <div className="ledger__id">
                <span className="row__title">{opening.title}</span>
                <span className="row__sub">
                  {opening.department}, {opening.location}, {opening.employmentType}
                </span>
              </div>
              <code className="slug">/careers/{opening.slug}</code>
              <span className={`pill pill--${opening.status === 'open' ? 'up' : opening.status === 'closed' ? 'down' : 'pending'}`}>
                {opening.status}
              </span>

              <span className="button-row">
                {mayWrite && opening.status !== 'open' ? (
                  <button
                    type="button"
                    className="button button--small"
                    aria-label={`Publish ${opening.title}`}
                    onClick={() => void attempt(() => platform.jobs.publish(organizationId, opening.id))}
                  >
                    Publish
                  </button>
                ) : null}

                {mayWrite && opening.status === 'open' ? (
                  <button
                    type="button"
                    className="button button--small"
                    aria-label={`Close ${opening.title}`}
                    onClick={() => void attempt(() => platform.jobs.close(organizationId, opening.id))}
                  >
                    Close
                  </button>
                ) : null}

                {mayWrite && opening.status === 'draft' && !opening.publishedAt ? (
                  <button
                    type="button"
                    className="button button--small button--danger"
                    aria-label={`Delete ${opening.title}`}
                    onClick={() => void attempt(() => platform.jobs.remove(organizationId, opening.id))}
                  >
                    Delete
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {problem ? (
        <p className="notice notice--problem" role="alert" data-testid="jobs-problem">
          {problem}
        </p>
      ) : null}

      {mayWrite ? (
        <form className="panel" onSubmit={create} aria-labelledby="new-opening-heading">
          <div className="panel__head">
            <div>
              <h2 id="new-opening-heading">New opening</h2>
              <p className="panel__hint">It stays a draft, and off the careers page, until you publish it.</p>
            </div>
          </div>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="job-title">Title</label>
              <input
                id="job-title"
                required
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="job-slug">Address</label>
              <span className="affix">
                <span className="affix__prefix">/careers/</span>
                <input
                  id="job-slug"
                  required
                  placeholder="design-lead"
                  value={draft.slug}
                  onChange={(event) => setDraft({ ...draft, slug: event.target.value })}
                />
              </span>
            </div>
            <div className="field">
              <label htmlFor="job-department">Department</label>
              <input
                id="job-department"
                required
                value={draft.department}
                onChange={(event) => setDraft({ ...draft, department: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="job-location">Location</label>
              <input
                id="job-location"
                required
                value={draft.location}
                onChange={(event) => setDraft({ ...draft, location: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="job-type">Type</label>
              <input
                id="job-type"
                required
                value={draft.employmentType}
                onChange={(event) => setDraft({ ...draft, employmentType: event.target.value })}
              />
            </div>
            <div className="field field--wide">
              <label htmlFor="job-description">Description</label>
              <textarea
                id="job-description"
                required
                rows={3}
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </div>
            <div className="field field--half">
              <label htmlFor="job-requirements">Requirements, one per line</label>
              <textarea
                id="job-requirements"
                rows={4}
                value={draft.requirements as unknown as string}
                onChange={(event) =>
                  setDraft({ ...draft, requirements: event.target.value as unknown as string[] })
                }
              />
            </div>
            <div className="field field--half">
              <label htmlFor="job-responsibilities">Responsibilities, one per line</label>
              <textarea
                id="job-responsibilities"
                rows={4}
                value={draft.responsibilities as unknown as string}
                onChange={(event) =>
                  setDraft({ ...draft, responsibilities: event.target.value as unknown as string[] })
                }
              />
            </div>
          </div>
          <div className="panel__foot">
            <button className="button button--primary" type="submit" disabled={busy}>
              <Icon name="plus" size={16} />
              {busy ? 'Saving…' : 'Create draft'}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

function splitLines(value: string | string[]): string[] {
  if (Array.isArray(value)) return value;
  return (value ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function describe(error: unknown): string {
  if (error instanceof PlatformApiError) {
    // The API writes these for a person and they carry no internals.
    if (error.status === 409 || error.status === 400) return error.message;
    if (error.status === 403) return 'Your role may not do that.';
    if (error.status === 404) return 'That opening is no longer there.';
  }
  return 'Something went wrong.';
}
